"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { balanceService, BalancesResponse } from "@/lib/balance-service";
import { isFirebaseInitialized, auth } from "@/lib/firebase";
import { onAuthStateChanged } from "firebase/auth";

interface BalanceContextType {
  balances: BalancesResponse | undefined;
  loading: boolean;
  refreshBalance: () => Promise<void>;
}

const BalanceContext = createContext<BalanceContextType | undefined>(undefined);

export const BalanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [balances, setBalances] = useState<BalancesResponse>();
  const [loading, setLoading] = useState(false);

  const refreshBalance = useCallback(async () => {
    if (!isFirebaseInitialized()) {
      console.warn("Firebase not initialized, skipping balance refresh");
      return;
    }

    setLoading(true);
    try {
      const balancesData = await balanceService.getBalances();
      setBalances(balancesData);
    } catch (error) {
      console.error("Failed to refresh balance:", error);
      // Можно добавить retry логику здесь
    } finally {
      setLoading(false);
    }
  }, []);

  // Автоматически загружаем баланс при монтировании и при изменении пользователя
  useEffect(() => {
    if (!isFirebaseInitialized() || !auth) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        void refreshBalance();
      } else {
        // Очищаем баланс при выходе
        setBalances(undefined);
      }
    });

    return () => unsubscribe();
  }, [refreshBalance]);

  return (
    <BalanceContext.Provider value={{ balances, loading, refreshBalance }}>
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (!context) {
    throw new Error("useBalance must be used within a BalanceProvider");
  }
  return context;
};
